import type { Tool } from '../../types/tools'

export const ANALYSIS_TOOLS: Tool[] = [
  {
    slug: 'financial-analysis',
    name: 'Financial Analysis',
    icon: 'line-chart',
    categories: ['evaluate', 'monitor'],
    featured: false,
    cardDescription:
      'Assess revenue, burn, runway and unit economics from the financials a company provides.',
    seo: {
      title: 'AI Startup Financial Analysis: Burn, Runway & Unit Economics | DiligenceGPT™',
      description:
        'Analyze startup financials for venture diligence. DiligenceGPT™ helps investors assess revenue, burn rate, runway, SaaS unit economics and the assumptions behind a company’s plan.',
      keywords: ['AI startup financial analysis', 'startup runway calculator', 'burn rate analysis', 'SaaS unit economics analysis'],
    },
    hero: {
      supporting:
        'Assess revenue, burn, runway, unit economics, and financial assumptions from company-provided information.',
      bullets: [
        'See revenue, burn and runway at a glance',
        'Check unit economics against the plan',
        'Surface the assumptions the model depends on',
      ],
    },
    whatIs: {
      question: 'What is AI startup financial analysis?',
      paragraphs: [
        'AI startup financial analysis helps investors interpret the financial information a company shares — historical results, forecasts and operating metrics — to understand performance, efficiency and the assumptions behind the plan.',
        'DiligenceGPT™’s Financial Analysis is for analysts and associates reviewing a company’s numbers during diligence or portfolio monitoring. You provide company-provided financials and metrics. The output summarizes revenue, burn and runway, calculates unit economics where the inputs allow, and lists the key assumptions and data gaps in the company’s plan.',
        'Results depend entirely on the information the company provides. The analysis does not audit or verify figures; it makes calculations and assumptions transparent so your team can test them.',
      ],
    },
    steps: [
      {
        title: 'Provide company financials',
        body: 'Add the financial statements, forecasts and operating metrics the company has shared.',
      },
      {
        title: 'Run the analysis',
        body: 'DiligenceGPT™ summarizes performance, calculates burn, runway and unit economics, and identifies plan assumptions.',
      },
      {
        title: 'Review and refine',
        body: 'Check calculations against the source, adjust assumptions, and note questions for the company.',
      },
    ],
    outputs: [
      { title: 'Revenue summary', body: 'Recent revenue and growth as reported, with the period clearly stated.' },
      { title: 'Burn and runway', body: 'Net burn and implied runway based on the cash and spend figures provided.' },
      { title: 'Unit economics', body: 'Metrics such as CAC payback, gross margin and LTV:CAC where inputs are available.' },
      { title: 'Plan assumptions', body: 'The growth, hiring and pricing assumptions the forecast relies on.' },
      { title: 'Data gaps and questions', body: 'Missing inputs and questions to send to the company.' },
    ],
    preview: {
      kind: 'Financial summary',
      title: 'Financial snapshot',
      subject: 'Tallyfox · Series A · AP automation — sample figures',
      metrics: [
        { label: 'Reported ARR', value: '$3.1M', note: 'Company-provided' },
        { label: 'Net burn / month', value: '$240K', note: 'Trailing 3 months' },
        { label: 'Implied runway', value: '14 mo', note: 'Cash ÷ net burn' },
        { label: 'Gross margin', value: '74%', note: 'As reported' },
      ],
      assumptions: ['Forecast assumes sales headcount doubles in 12 months', 'Pricing holds as company moves upmarket'],
      gaps: ['CAC by channel', 'Cohort-level retention'],
    },
    process: {
      heading: 'Analyze against the metrics your fund tracks',
      body: 'Focus on the metrics and benchmarks your team uses for a given stage and business model. The same structure carries into portfolio reporting once a company is in your portfolio.',
      configurable: ['Key metrics by stage and model', 'Runway and burn thresholds', 'Questions template for founders'],
      next: { slug: 'valuation-comps', prompt: 'Reviewed the financials? Explore valuation and comps next.' },
    },
    faqs: [
      { question: 'What does Financial Analysis do?', answer: 'It summarizes a startup’s company-provided financial information, calculates measures such as burn, runway and unit economics where inputs allow, and highlights plan assumptions and data gaps.' },
      { question: 'What information do I need?', answer: 'The financial statements, forecasts and operating metrics the company has shared. Calculations are only as complete as those inputs.' },
      { question: 'Does it verify the company’s numbers?', answer: 'No. It works from company-provided information and does not audit or verify figures. Use it to make calculations and assumptions transparent, then confirm with the company and specialists as needed.' },
      { question: 'How should analysts review the results?', answer: 'Check each calculation against the source figures, test the assumptions that drive the forecast, and send open questions to the company.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'It typically follows data room review, feeds valuation work and your investment memo, and can be reused when monitoring portfolio companies.' },
    ],
    finalCta: {
      heading: 'See what the numbers actually say.',
      body: 'Summarize performance, burn and unit economics with every assumption on the table.',
    },
    related: ['valuation-comps', 'data-room-review', 'investment-memo-writer', 'portfolio-reporting', 'pitch-deck-screener', 'market-research', 'competitor-analysis', 'founder-research', 'deal-sourcing'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 340,
  },
  {
    slug: 'valuation-comps',
    name: 'Valuation & Comps',
    icon: 'scale',
    categories: ['evaluate'],
    featured: false,
    cardDescription:
      'Explore valuation assumptions and relevant company or funding benchmarks for your assessment.',
    seo: {
      title: 'AI Startup Valuation & Funding Comparables | DiligenceGPT™',
      description:
        'Explore startup valuation assumptions and funding comparables. DiligenceGPT™ helps venture investors organize benchmarks and make valuation reasoning explicit, with data limitations clearly noted.',
      keywords: ['AI startup valuation', 'venture capital valuation tool', 'startup funding comparables', 'private company valuation analysis'],
    },
    hero: {
      supporting:
        'Explore valuation assumptions and relevant company or funding benchmarks to support your investment assessment.',
      bullets: [
        'Lay out valuation reasoning step by step',
        'Organize relevant company and funding benchmarks',
        'See where benchmark data is limited',
      ],
    },
    whatIs: {
      question: 'What is an AI startup valuation tool?',
      paragraphs: [
        'An AI startup valuation tool helps investors reason about what a private company may be worth by organizing the assumptions behind a valuation and comparing the company with relevant benchmarks — similar companies and comparable funding rounds.',
        'DiligenceGPT™’s Valuation & Comps is for investors preparing a view on price and terms. You provide the company, its financial and round information, and the comparables or criteria you consider relevant. It organizes benchmarks that are available to it, lays out valuation reasoning and its assumptions, and states where comparable data is limited or uncertain.',
        'Private-market valuation data is often incomplete, self-reported or unavailable. This tool does not guarantee access to proprietary transaction databases and does not produce a definitive valuation; it supports your team’s judgment.',
      ],
    },
    steps: [
      {
        title: 'Provide company and round details',
        body: 'Add the company’s financial information, the proposed round terms if known, and the comparables you consider relevant.',
      },
      {
        title: 'Run the analysis',
        body: 'DiligenceGPT™ organizes available benchmarks and lays out valuation reasoning with each assumption explicit.',
      },
      {
        title: 'Review and refine',
        body: 'Adjust the comparable set and assumptions, and weigh the results alongside your team’s own pricing view.',
      },
    ],
    outputs: [
      { title: 'Valuation assumptions', body: 'The inputs a valuation view depends on, stated explicitly so they can be challenged.' },
      { title: 'Comparable companies', body: 'Relevant companies grouped by business model, stage and sector, where information is available.' },
      { title: 'Funding benchmarks', body: 'Comparable round information from available sources, with each source noted.' },
      { title: 'Sensitivity view', body: 'How the valuation view changes as key assumptions move.' },
      { title: 'Data limitations', body: 'Where benchmarks are sparse, dated or self-reported.' },
    ],
    preview: {
      kind: 'Comparables analysis',
      title: 'Valuation benchmarks',
      subject: 'Tallyfox · Series A · AP automation — sample, fictional comps',
      table: {
        caption: 'Sample comparables — fictional',
        columns: ['Comparable', 'Stage', 'Revenue multiple', 'Data quality'],
        rows: [
          ['Comp A (B2B fintech)', 'Series A', '9–12×', 'Reported'],
          ['Comp B (AP software)', 'Series A', '11×', 'Partial'],
          ['Comp C (spend mgmt)', 'Series B', '14×', 'Limited'],
        ],
      },
      assumptions: ['Revenue multiple applied to reported ARR', 'Comparable set limited to North American rounds'],
      gaps: ['Sparse recent comparable rounds', 'Comp B multiple derived from partial disclosure'],
    },
    process: {
      heading: 'Benchmarks framed by your investment approach',
      body: 'Define what counts as a relevant comparable for your fund and how you prefer to frame valuation, so analysis is consistent across deals. Bring the results into the investment memo.',
      configurable: ['Comparable selection criteria', 'Preferred valuation framing', 'Ownership and return targets'],
      next: { slug: 'investment-memo-writer', prompt: 'Explored valuation? Bring it together in an investment memo.' },
    },
    faqs: [
      { question: 'What does Valuation & Comps do?', answer: 'It organizes valuation assumptions and available company and funding benchmarks to support your team’s view on a private company’s valuation.' },
      { question: 'Does it provide a definitive valuation?', answer: 'No. It supports your analysis by making assumptions and benchmarks explicit. Valuation of private companies involves judgment and the final view is your team’s.' },
      { question: 'Where does comparable data come from?', answer: 'From the information you provide and other sources available to the tool, with sources noted. Private-market data is often incomplete, and access to proprietary transaction databases is not guaranteed.' },
      { question: 'How should analysts review the results?', answer: 'Check each comparable for relevance, review the data quality notes, and test how sensitive the view is to key assumptions.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'It usually follows financial analysis and competitor mapping, and informs the valuation and terms discussion in your investment memo.' },
    ],
    finalCta: {
      heading: 'Price the round with your reasoning in view.',
      body: 'Organize benchmarks and assumptions so your team can debate the number with clarity.',
    },
    related: ['financial-analysis', 'competitor-analysis', 'investment-memo-writer', 'market-research', 'data-room-review', 'pitch-deck-screener', 'founder-research', 'portfolio-reporting', 'deal-sourcing'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 210,
  },
]
